import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Chip, 
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs
} from '@mui/material';
import { ArrowBack, CalendarToday, Html, CheckCircle, Speed } from '@mui/icons-material';
import { useBlogPost } from '../hooks/useBlogHTML';
import { useAdmin } from '../contexts/AdminContext';
import { StoryChiefPost } from '../types/blog';

export const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isAdminMode } = useAdmin();
  
  // Add this debug logging
  console.log('BlogPost component loaded with slug:', slug);
  
  const { post, loading, error, htmlGenerated, fromCache } = useBlogPost(slug || '');
  
  // Add this debug logging
  console.log('useBlogPost returned:', { post: !!post, loading, error, htmlGenerated, fromCache });

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date unavailable';
    }
  };

  // Helper function to get the image URL from the nested structure
  const getPostImage = (post: StoryChiefPost): string | null => {
    if (post?.featured_image?.data?.url) {
      return post.featured_image.data.url;
    }
    
    if (post?.featured_image?.data?.sizes) {
      const sizes = post.featured_image.data.sizes;
      return sizes.large || sizes.regular || sizes.full;
    }
    
    return null;
  };

  const getImageAlt = (post: StoryChiefPost): string => {
    return post?.featured_image?.data?.alt || 
           post?.featured_image?.data?.name || 
           post?.title || 
           'Blog post image';
  };

  const getPostExcerpt = (post: StoryChiefPost): string => {
    return post?.excerpt || 'No description available';
  };

  // Helper function to get keywords for display
  const getPostKeywords = (post: StoryChiefPost): string[] => {
    if (!post.seo_keyword) return [];
    return post.seo_keyword.split(',').map(k => k.trim()).filter(k => k);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" py={8}>
          <CircularProgress sx={{ mr: 2 }} />
          <Typography>
            {fromCache ? 'Loading from cache...' : 'Loading post...'}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !post) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Post Not Found</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {error || 'The blog post you are looking for does not exist.'}
          </Typography>
          <Button 
            component={Link}
            to="/blog"
            variant="contained"
            size="small"
          >
            Back to Blog
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Breadcrumbs sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Typography color="text.primary">Home</Typography>
        </Link>
        <Link to="/blog" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Typography color="text.primary">Blog</Typography>
        </Link>
        <Typography color="text.secondary">{post.title}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Button
          component={Link}
          to="/blog"
          startIcon={<ArrowBack />}
        >
          Back to Blog
        </Button>

        {/* Admin Mode: Technical indicators */}
        {isAdminMode && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {fromCache && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Speed sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="caption" color="success.main">
                  Cached
                </Typography>
              </Box>
            )}
            
            {htmlGenerated && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="caption" color="success.main">
                  HTML Generated
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Html />}
                  href={`/blog/${slug}.html`}
                  target="_blank"
                  sx={{ ml: 1 }}
                >
                  View HTML
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Admin Mode: Performance indicator */}
      {isAdminMode && fromCache && (
        <Alert severity="success" sx={{ mb: 4 }}>
          <Typography variant="body2">
            ⚡ This post was loaded from cache for faster performance. No API call was needed!
          </Typography>
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        {getPostImage(post) && (
          <Box
            component="img"
            src={getPostImage(post)!}
            alt={getImageAlt(post)}
            sx={{
              width: '100%',
              height: 400,
              objectFit: 'cover',
              borderRadius: 2,
              mb: 4
            }}
            onError={(e) => {
              console.error('Image failed to load:', getPostImage(post));
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, color: 'text.secondary' }}>
          <CalendarToday fontSize="small" />
          <Typography variant="body2">
            {formatDate(post.published_at)}
          </Typography>
        </Box>

        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
          {post.title}
        </Typography>

        {post.excerpt && (
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, fontWeight: 400 }}>
            {getPostExcerpt(post)}
          </Typography>
        )}

        {getPostKeywords(post).length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
            {getPostKeywords(post).map((keyword, index) => (
              <Chip 
                key={index} 
                label={keyword} 
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        )}

        <Box 
          sx={{ 
            '& img': { maxWidth: '100%', height: 'auto' },
            '& a': { color: 'primary.main' },
            '& h1, & h2, & h3, & h4, & h5, & h6': { 
              fontFamily: 'inherit',
              fontWeight: 600,
              mt: 3,
              mb: 2
            },
            '& p': { mb: 2, lineHeight: 1.7 },
            '& ul, & ol': { mb: 2, pl: 3 },
            '& li': { mb: 1 },
            '& blockquote': { 
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              ml: 0,
              fontStyle: 'italic',
              color: 'text.secondary'
            },
            '& code': {
              backgroundColor: 'grey.100',
              padding: '2px 6px',
              borderRadius: 1,
              fontSize: '0.875em'
            },
            '& pre': {
              backgroundColor: 'grey.100', 
              p: 2,
              borderRadius: 1,
              overflow: 'auto'
            }
          }}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      </Box>

      {/* Hidden admin trigger */}
      <Box sx={{ textAlign: 'center', mt: 8, py: 4 }}>
        <Typography 
          variant="caption" 
          color="text.disabled"
          sx={{ 
            cursor: 'default', 
            userSelect: 'none',
            '&:hover': { color: 'text.secondary' }
          }}
          data-admin-trigger
          title="Click to toggle admin mode"
        >
          © 2025 RECRUITMENT INTELLIGENCE®. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};